import React from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { BodyTextFieldsFragment } from 'api/graphql-types';

import { getFormattedDate } from 'utils/date';
import { sanitizeAndParseHtml } from 'utils/rich-text-renderer/sanitize-and-parse';

import { renderDownloadLink } from 'modules/downloads/downloads';
import { SidebarLink } from 'components/sidebar-link/sidebar-link';

import styles from './normal-body-text.module.scss';
import bodyTextStyles from '../body-text.module.scss';
import Typography from 'components/typography';

interface IBodyTextProps {
	module: BodyTextFieldsFragment;
	publishedAt?: string;
	updatedAt?: string;
	small?: boolean;
	boldIntro?: boolean;
}

const BodyText = (props: IBodyTextProps): React.JSX.Element => {
	const router = useRouter();
	const { locale } = router;
	const intl = useIntl();

	const isSidebarEnabled = props.module.sidebar != null || props.small;
	const parsedHtml = sanitizeAndParseHtml(props.module.bodyText || '', props.boldIntro);
	const formattedDate =
		props.publishedAt || props.updatedAt
			? getFormattedDate(new Date((props.publishedAt as string) || (props.updatedAt as string)), 'd-MMMM-y', locale)
			: '';

	return (
		<div className={clsx('container', bodyTextStyles.bodyText, styles.row, styles.rowWithSidebar)}>
			<section className={styles.content}>
				{props.publishedAt && (
					<span>
						{intl.formatMessage({ id: 'modules.bodyText.publishedAt' })}: {formattedDate}
					</span>
				)}
				{props.updatedAt && (
					<span>
						{intl.formatMessage({ id: 'modules.bodyText.updatedAt' })}: {formattedDate}
					</span>
				)}
				{parsedHtml}
			</section>
			{isSidebarEnabled && (
				<aside className={clsx(styles.sidebar, !!props.module.sidebar?.title?.value && styles.withTitle)}>
					{props.module.sidebar?.title?.value && (
						<Typography tag={props.module.sidebar.title?.type || 'div'} variant="h4">
							{props.module.sidebar.title.value}
						</Typography>
					)}
					<ul>
						{props.module.sidebar?.links?.map((link, idx) => (
							<li key={link?.title} aria-labelledby={`description${idx}`}>
								<SidebarLink
									href={link?.href?.linkUrl || link?.href?.url}
									description={link?.description}
									title={link?.href?.linkText || link?.href?.text || link?.title}
								/>
							</li>
						))}
						{props.module.sidebar?.files?.map((file) => (
							<li key={file?.data?.attributes?.hash} className={styles.file}>
								<SidebarLink
									href={file?.data?.attributes?.url}
									description={file?.data?.attributes?.caption}
									title={renderDownloadLink(
										{
											ext: file?.data?.attributes?.ext || '',
											size: file?.data?.attributes?.size || 0
										},
										file?.data?.attributes?.name
									)}
								/>
							</li>
						))}
					</ul>
				</aside>
			)}
		</div>
	);
};

export default BodyText;
